'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { CIRRUS } from 'HubStyleTokens/colors';
import I18n from 'I18n';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIAlert from 'UIComponents/alert/UIAlert';
import UIButton from 'UIComponents/button/UIButton';
import UIErrorMessage from 'UIComponents/error/UIErrorMessage';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import LoadingWrapper from 'ecosystem-marketplace-core/components/LoadingWrapper';
import DocumentHead from 'ecosystem-marketplace-core/components/portals/DocumentHead';
import PrerenderReady from 'ecosystem-marketplace-core/components/prerender/PrerenderReady';
import MainSection from 'ecosystem-marketplace-core/components/layout/MainSection';
import MainSectionInner from 'ecosystem-marketplace-core/components/layout/MainSectionInner';
import MediaSize from 'ecosystem-marketplace-core/components/layout/MediaSize';
import * as ScreenSizes from 'ecosystem-marketplace-core/constants/layout/ScreenSizes';
import { useHeaderTabs } from 'marketplace-ui-common/hooks/useHeaderTabs';
import { useScrollEventListener } from 'ecosystem-marketplace-core/hooks/useScrollEventListener';
import { getIsPrerenderUserAgent } from 'ecosystem-marketplace-core/utils/userAgent';
import { useScrollToTop } from 'ecosystem-marketplace-core/hooks/useScrollToTop';
import { isSucceeded } from 'ecosystem-marketplace-core/utils/requestState';
import { getProfileListing, clearProfileListing } from '../actions/ProfileActions';
import { updateReviewFilters } from '../actions/ReviewActions';
import { CARD_PADDING, PROFILE_LEFT_COL_SIZE, PROFILE_RIGHT_COL_SIZE, LOGO_WRAPPER_OFFSET } from '../constants/ProfileConstants';
import { getCanonicalAppContext, getIsPublicApp } from '../selectors/app';
import { getProfileListingDetails, getProfileListingRequestError, getProfileListingRequestState, getProfileReviewSummaryRequestState } from '../selectors/profile';
import { getReviewsRequestState } from '../selectors/reviews';
import { getCertificationsRequestState } from '../selectors/certifications';
import { getAccreditations } from '../selectors/accreditations';
import { getIsProfileListingAccredited } from '../utils/credentials';
import { getProfilePageTitleText } from '../utils/i18n';
import { getInAppReviewLink } from '../utils/urls';
import { track } from '../utils/usageTracker';
import UsageViewTracker from '../components/tracking/UsageViewTracker';
import AboutCard from '../components/profile/AboutCard';
import ReviewsCard from '../components/profile/ReviewsCard';
import ContactInfoCard from '../components/profile/ContactInfoCard';
import GeneralInfoCard from '../components/profile/GeneralInfoCard';
import ProfileHeader from '../components/profile/ProfileHeader';
import ProfilePageMeta from '../components/profile/ProfilePageMeta';
import GenericError from '../components/error/GenericError';
import { addViewedProfile } from '../utils/homepage';

const ProfileLoadError = () => /*#__PURE__*/_jsxs(NavMarker, {
  name: "PROFILE_404_LOAD",
  children: [/*#__PURE__*/_jsx(DocumentHead, {
    children: /*#__PURE__*/_jsx(PrerenderReady, {
      statusCode: 404
    })
  }), /*#__PURE__*/_jsx(UIErrorMessage, {
    type: "notFound"
  }), /*#__PURE__*/_jsx(UIFlex, {
    justify: "center",
    children: /*#__PURE__*/_jsx(UIRouterLink, {
      use: "unstyled",
      to: "/",
      children: /*#__PURE__*/_jsx(UIButton, {
        use: "tertiary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystem-marketplace-solutions-ui.profile.back"
        })
      })
    })
  })]
});

const ProfileLoadingSpinner = () => /*#__PURE__*/_jsx(UIFlex, {
  className: "p-top-15",
  style: {
    width: '100vw'
  },
  justify: "center",
  children: /*#__PURE__*/_jsx(UILoadingSpinner, {})
});

const Profile = ({
  location: {
    pathname,
    search
  },
  match: {
    params: {
      profileSlug: slug
    }
  }
}) => {
  useScrollToTop(pathname);
  const canonicalAppContext = useSelector(getCanonicalAppContext);
  const isPublicApp = useSelector(getIsPublicApp);
  const isReviewLink = pathname.includes('/write-review');
  const isPreview = pathname === '/_preview';
  const isPrerender = getIsPrerenderUserAgent();
  const reviewButtonRef = useRef(null);
  const profileListingRequestError = useSelector(getProfileListingRequestError);
  const profileListingRequestState = useSelector(getProfileListingRequestState);
  const profileReviewSummaryRequestState = useSelector(getProfileReviewSummaryRequestState);
  const profileListing = useSelector(getProfileListingDetails);
  const accreditations = useSelector(getAccreditations);
  const isProfileAccredited = getIsProfileListingAccredited(accreditations, profileListing);
  const profileReviewsRequestState = useSelector(getReviewsRequestState);
  const certificationsRequestState = useSelector(getCertificationsRequestState);
  const dispatch = useDispatch();
  const tabIds = ['about', 'reviews'];
  const scrollOffset = LOGO_WRAPPER_OFFSET + CARD_PADDING;
  const {
    calculateActiveTab,
    scrollToTabSection,
    activeTab
  } = useHeaderTabs(tabIds, scrollOffset);
  useScrollEventListener(calculateActiveTab);
  useEffect(() => () => dispatch(clearProfileListing()), [dispatch]);
  useEffect(() => {
    addViewedProfile(profileListing.name);
  }, [profileListing.name]);
  const getUsageEventProperties = useCallback((extras = {}) => isProfileAccredited ? Object.assign({}, extras, {
    screen: 'profile',
    tags: ['accredited']
  }) : Object.assign({}, extras, {
    screen: 'profile'
  }), [isProfileAccredited]);
  const handleTrackInteraction = useCallback((action, extras = {}, trackFn = track) => {
    trackFn('profile-interaction', getUsageEventProperties(Object.assign({
      action
    }, extras)));
  }, [getUsageEventProperties]);
  const handleScrollToTabSection = useCallback(tabSection => {
    scrollToTabSection(tabSection);

    if (tabSection === 'reviews') {
      reviewButtonRef.current.focus();
    }
  }, [reviewButtonRef, scrollToTabSection]);
  const renderedGeneralInfoCard = useMemo(() => {
    if (!profileListing) {
      return null;
    }

    return /*#__PURE__*/_jsx(GeneralInfoCard, {
      profileListing: profileListing
    });
  }, [profileListing]);
  useEffect(() => {
    if (isPublicApp && isReviewLink) {
      window.location.href = getInAppReviewLink(slug, search);
    }
  }, [isPublicApp, isReviewLink, slug, search]);
  useEffect(() => {
    dispatch(getProfileListing(slug, isPreview));
  }, [dispatch, isPreview, slug]);
  useEffect(() => {
    if (isSucceeded(profileListingRequestState)) {
      document.title = getProfilePageTitleText(profileListing, I18n.text);
    }
  }, [profileListing, profileListingRequestState]);
  useEffect(() => {
    if (isSucceeded(profileListingRequestState)) {
      const {
        id: profileId
      } = profileListing; // This filter update causes the filters middleware
      // to fetch initial page load of reviews

      dispatch(updateReviewFilters({
        entityId: profileId
      }));
    }
  }, [dispatch, profileListingRequestState, profileListing]);

  if (profileListingRequestError) {
    const {
      status: statusCode
    } = profileListingRequestError;

    if (statusCode === 404) {
      return /*#__PURE__*/_jsx(ProfileLoadError, {});
    } else {
      return /*#__PURE__*/_jsxs(NavMarker, {
        name: "PROFILE_ERROR_LOAD",
        children: [/*#__PURE__*/_jsx(DocumentHead, {
          children: /*#__PURE__*/_jsx(PrerenderReady, {
            statusCode: statusCode
          })
        }), /*#__PURE__*/_jsx(GenericError, {})]
      });
    }
  }

  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(ProfilePageMeta, {
      canonicalAppContext: canonicalAppContext,
      certificationsRequestState: certificationsRequestState,
      pathname: pathname,
      profileListing: profileListing,
      profileListingRequestState: profileListingRequestState,
      profileReviewsRequestState: profileReviewsRequestState,
      profileReviewSummaryRequestState: profileReviewSummaryRequestState
    }), /*#__PURE__*/_jsx(LoadingWrapper, {
      LoadingComponent: ProfileLoadingSpinner,
      ErrorComponent: ProfileLoadError,
      requestStatus: profileListingRequestState,
      children: /*#__PURE__*/_jsxs(NavMarker, {
        name: "PROFILE_LOAD",
        children: [/*#__PURE__*/_jsxs("main", {
          style: {
            backgroundColor: CIRRUS,
            minHeight: '100vh'
          },
          children: [isPreview && /*#__PURE__*/_jsx(UIAlert, {
            titleText: unescapedText('ecosystem-marketplace-solutions-ui.profile.previewAlert.titleText'),
            type: "warning",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "ecosystem-marketplace-solutions-ui.profile.previewAlert.message"
            })
          }), /*#__PURE__*/_jsx(ProfileHeader, {
            activeTab: activeTab,
            canonicalAppContext: canonicalAppContext,
            isPrerender: isPrerender,
            isPreview: isPreview,
            onTrackInteraction: handleTrackInteraction,
            profileListing: profileListing,
            scrollToTabSection: handleScrollToTabSection
          }), /*#__PURE__*/_jsx(MainSection, {
            className: "p-top-0",
            color: CIRRUS,
            responsive: true,
            children: /*#__PURE__*/_jsx(MainSectionInner, {
              children: /*#__PURE__*/_jsxs(UIGrid, {
                children: [/*#__PURE__*/_jsxs(UIGridItem, {
                  size: PROFILE_LEFT_COL_SIZE,
                  children: [/*#__PURE__*/_jsx(ContactInfoCard, {
                    onTrackInteraction: handleTrackInteraction,
                    profileListing: profileListing,
                    scrollToTabSection: handleScrollToTabSection
                  }), /*#__PURE__*/_jsx(MediaSize, {
                    minScreenSize: ScreenSizes.MD,
                    children: renderedGeneralInfoCard
                  })]
                }), /*#__PURE__*/_jsxs(UIGridItem, {
                  size: PROFILE_RIGHT_COL_SIZE,
                  children: [/*#__PURE__*/_jsx(AboutCard, {
                    profileListing: profileListing
                  }), /*#__PURE__*/_jsx(ReviewsCard, {
                    isPreview: isPreview,
                    onTrackInteraction: handleTrackInteraction,
                    profileListing: profileListing,
                    reviewButtonRef: reviewButtonRef,
                    scrollToTabSection: handleScrollToTabSection
                  }), /*#__PURE__*/_jsx(MediaSize, {
                    maxScreenSize: ScreenSizes.MD,
                    children: renderedGeneralInfoCard
                  })]
                })]
              })
            })
          })]
        }), /*#__PURE__*/_jsx(UsageViewTracker, {
          name: "profile-pageview",
          properties: getUsageEventProperties
        })]
      })
    })]
  });
};

Profile.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      profileSlug: PropTypes.string
    }).isRequired
  }).isRequired
};
export default Profile;